import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "pending", "shortlisted", "rejected"];

const reviewOf = (a) => a.review_status || "pending";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("kos_admin_token")}` }),
    []
  );

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/applications`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setApps(data.applications);
    } catch (err) {
      setError(err.message);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(
    () => ({
      total: apps.length,
      pending: apps.filter((a) => reviewOf(a) === "pending").length,
      shortlisted: apps.filter((a) => reviewOf(a) === "shortlisted").length,
      rejected: apps.filter((a) => reviewOf(a) === "rejected").length,
    }),
    [apps]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter(
      (a) =>
        (filter === "all" || reviewOf(a) === filter) &&
        (!q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
    );
  }, [apps, filter, query]);

  const patchStatus = async (id, review_status) => {
    setBusyId(id);
    setError("");
    try {
      const res = await fetch(`${API}/applications/${id}/status`, {
        method: "PATCH",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ status: review_status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setApps((list) => list.map((a) => (a.id === id ? { ...a, review_status } : a)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    setBusyId(id);
    setError("");
    try {
      const res = await fetch(`${API}/applications/${id}`, { method: "DELETE", headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setApps((list) => list.filter((a) => a.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async () => {
    setError("");
    try {
      const res = await fetch(`${API}/applications/export.csv?status=${filter}`, { headers: headers() });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(formatApiErrorDetail(data.detail));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `koslab-2027-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-topbar">
        <div className="wrap">
          <a className="brand" href="/" data-testid="admin-brand">
            <span className="mark">KOS-LAB</span>
            <span>
              KOSS 2027
              <small>APPLICATIONS DASHBOARD</small>
            </span>
          </a>
          <div className="admin-user">
            <span data-testid="admin-user-email">{user.email}</span>
            <button className="btn outline admin-logout" onClick={logout} data-testid="admin-logout-btn">
              Sign out
            </button>
          </div>
        </div>
      </div>
      <div className="wrap admin-body">
        <div className="admin-stats">
          <div className="admin-stat" data-testid="stat-total"><strong>{stats.total}</strong><span>Total</span></div>
          <div className="admin-stat" data-testid="stat-pending"><strong>{stats.pending}</strong><span>Pending</span></div>
          <div className="admin-stat" data-testid="stat-shortlisted"><strong>{stats.shortlisted}</strong><span>Shortlisted</span></div>
          <div className="admin-stat" data-testid="stat-rejected"><strong>{stats.rejected}</strong><span>Rejected</span></div>
        </div>
        {error && (
          <div className="notice err" role="alert" data-testid="admin-error">{error}</div>
        )}
        <div className="admin-toolbar">
          <div className="filter-pills">
            {FILTERS.map((f) => (
              <button
                key={f}
                className={`pill ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <input
            className="admin-search"
            placeholder="Search name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-search-input"
          />
          <button className="mini-btn export-btn" onClick={exportCsv} data-testid="export-csv-btn">
            Export CSV ↓
          </button>
        </div>
        {visible.length === 0 && (
          <p className="admin-empty" data-testid="admin-empty">No applications match this view yet.</p>
        )}
        {visible.map((a) => {
          const review = reviewOf(a);
          return (
            <div className="app-card" key={a.id} data-testid="application-row">
              <div className="app-card-head" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                <div>
                  <h3>{a.name}</h3>
                  <p className="app-meta">{a.email} · {a.phone || a.whatsapp} · Age {a.age}{a.gender ? ` · ${a.gender}` : ""}{a.status ? ` · ${a.status}` : ""}</p>
                  <p className="app-meta">{a.address || a.location} · Applied {new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`badge ${review}`} data-testid="application-status-badge">{review}</span>
              </div>
              {expanded === a.id && (
                <div className="app-detail" data-testid="application-detail">
                  {(a.essay || a.strategy) && (
                    <p><strong>{a.essay ? "Leadership essay" : "Strategy answer"}:</strong> {a.essay || a.strategy}</p>
                  )}
                  {a.address && (
                    <p>
                      <strong>In Osun:</strong> {a.osun ? "Yes" : "No"} · <strong>Final-year student:</strong>{" "}
                      {a.is_final_year ? "Yes" : "No"} · <strong>Graduate:</strong> {a.is_graduate ? "Yes" : "No"} ·{" "}
                      <strong>Headshot consent:</strong> {a.headshot_consent ? "Yes" : "No"}
                    </p>
                  )}
                  {[["proof_of_age", "Proof of age"], ["nin_id", "NIN ID card"], ["student_id", "Student ID"], ["grad_cert", "NYSC / Degree certificate"], ["cv", "CV / Resume"]].some(([k]) => a[k]) && (
                    <p className="doc-links">
                      <strong>Attachments:</strong>{" "}
                      {[["proof_of_age", "Proof of age"], ["nin_id", "NIN ID card"], ["student_id", "Student ID"], ["grad_cert", "NYSC / Degree certificate"], ["cv", "CV / Resume"]]
                        .filter(([k]) => a[k])
                        .map(([k, label]) => (
                          <a
                            key={k}
                            className="doc-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${API}/files/${a[k]}?auth=${localStorage.getItem("kos_admin_token")}`}
                            data-testid={`doc-link-${k}`}
                          >
                            {label} ↗
                          </a>
                        ))}
                    </p>
                  )}
                  <p><strong>Reference:</strong> {a.id.slice(0, 8).toUpperCase()}</p>
                </div>
              )}
              <div className="app-actions">
                {review !== "shortlisted" && (
                  <button className="mini-btn good" disabled={busyId === a.id} onClick={() => patchStatus(a.id, "shortlisted")} data-testid="shortlist-btn">
                    Shortlist
                  </button>
                )}
                {review !== "rejected" && (
                  <button className="mini-btn bad" disabled={busyId === a.id} onClick={() => patchStatus(a.id, "rejected")} data-testid="reject-btn">
                    Reject
                  </button>
                )}
                {review !== "pending" && (
                  <button className="mini-btn" disabled={busyId === a.id} onClick={() => patchStatus(a.id, "pending")} data-testid="pending-btn">
                    Back to pending
                  </button>
                )}
                <button className="mini-btn danger" disabled={busyId === a.id} onClick={() => remove(a.id)} data-testid="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
